import React from 'react';
import { InteractionInfo } from '$/components/Interactions/InteractionInfo';
import { AnimatePresence, motion } from 'framer-motion';
import { WebinarPlayerContext, WebinarPlayerUIState } from '../context';
import { css } from '@emotion/react';
import { useInteractionIconColor } from '$/hooks/useInteractionIconColor';
import { useThemeContext } from '$/components/ui/ThemeContext';
import tw from 'twin.macro';
import { withContextValue } from '$/hooks/withPartialContexts';
import { readableColorOnBackground } from '$/shared/frontend/utils/tinyColor';

export const DockIconTopPx = 15;
export const DockIconHeight = 50;

type DockIconProps = {
  interaction: InteractionInfo;
  index: number;
  snappedIdRef: React.RefObject<string | undefined>;
  onClickIcon: (i: InteractionInfo) => void;
  submitting: boolean;
  isExpired: boolean;
} & Pick<
  WebinarPlayerUIState,
  'ewebinar' | 'dimensions' | 'editMode' | 'isPlaying' | 'transitions'
>;

export const DockIcon = withContextValue<
  WebinarPlayerUIState,
  DockIconProps,
  Pick<
    WebinarPlayerUIState,
    'ewebinar' | 'dimensions' | 'editMode' | 'isPlaying' | 'transitions'
  > & {
    submitting: boolean;
    isExpired: boolean;
  }
>(
  WebinarPlayerContext,
  (
    {
      ewebinar,
      dimensions,
      editMode,
      isPlaying,
      willBeRemovedCards,
      playbackPosition,
      mode,
      transitions,
    },
    { interaction }
  ) => {
    return {
      ewebinar,
      dimensions,
      transitions,
      editMode,
      isPlaying,
      submitting: Boolean(interaction.id && willBeRemovedCards[interaction.id]),
      isExpired: interaction.isExpired(playbackPosition, mode),
    };
  }
)(({
  interaction,
  index,
  onClickIcon,
  snappedIdRef,
  ewebinar,
  dimensions,
  submitting,
  editMode,
  isPlaying,
  transitions,
  isExpired,
}) => {
  const isEditAndPaused = editMode && !isPlaying;
  const Icon = interaction.icon();
  const etheme = useThemeContext();

  const { color, secondaryColor, showBadge } = useInteractionIconColor(interaction, ewebinar, {
    forDock: true,
  });
  const snappedFgColor = readableColorOnBackground(ewebinar.actionColor);

  const applyExitTransition =
    interaction.shouldRemoveAnsweredCard() ||
    (interaction.removeCardOnAnswer() && submitting) ||
    (!interaction.shortId.includes('result') && isExpired);

  return (
    <>
      <motion.div
        key={`${interaction.id}-bg`}
        className='IconBoxBg'
        css={css`
          ${tw`rounded-lg`}
          position: absolute;
          top: ${DockIconTopPx + index * DockIconHeight}px;
          left: 0.5rem;
          width: ${dimensions.actionsPane.idock.widthRem - 1}rem;
          height: ${dimensions.actionsPane.idock.widthRem - 1}rem;
          background: ${etheme.player.dock.iconBg};
          z-index: 10;
          opacity: 0;
          -webkit-tap-highlight-color: transparent;
        `}
        exit={
          applyExitTransition
            ? {
                scale: 0,
                transition: transitions['icon.exit'],
              }
            : undefined
        }
        variants={
          applyExitTransition
            ? {}
            : {
                [`card.new.${interaction.id}`]: {
                  opacity: 0,
                },
                [`card.show.${interaction.id}`]: {
                  opacity: 1,
                },
                ['card.show.all']: {
                  opacity: 1,
                },
                ['icon.show.all']: {
                  opacity: 1,
                },
                ['icon.unsnap.all']: {
                  opacity: 1,
                },
              }
        }
      />

      <motion.div
        key={interaction.id}
        className={`dock-icon dock-icon-${interaction.shortId}`}
        css={css`
          justify-content: center;
          align-items: center;
          width: ${dimensions.actionsPane.idock.widthRem - 1}rem;
          height: ${DockIconHeight}px;
          padding-bottom: 7px;

          ${tw`flex cursor-pointer`}
          z-index: 20;
          opacity: 1;
          color: ${color};
          transform: translateY(0vh);
          --fa-secondary-color: ${secondaryColor};

          -webkit-tap-highlight-color: transparent;
          scroll-snap-align: center;
          &:first-of-type {
            margin-top: 0;
          }
          &:last-child {
            margin-bottom: 0;
          }
        `}
        onClick={(): void => onClickIcon(interaction)}
        whileHover={{
          scale: snappedIdRef.current === interaction.id ? undefined : 1.1,
        }}
        exit={
          applyExitTransition
            ? [`icon.exit.${interaction.id}`, `icon.afterExit.${interaction.id}`]
            : undefined
        }
        variants={
          applyExitTransition
            ? {
                [`icon.exit.${interaction.id}`]: {
                  transition: transitions['icon.exit'],
                  scale: 0,
                },
                [`icon.afterExit.${interaction.id}`]: {
                  height: 0,
                  paddingTop: 0,
                  transition: transitions['icon.afterExit'],
                },
              }
            : {
                [`card.new.${interaction.id}`]: {
                  y: 0,
                  scale: 0,
                  opacity: 0,
                },
                [`card.show.${interaction.id}`]: {
                  y: '0vh',
                  scale: 1,
                  opacity: 1,
                  transitionEnd: { opacity: 1 },
                },
                ['icon.unsnap.all']: {
                  y: '0vh',
                  scale: 1,
                  opacity: 1,
                  transitionEnd: { opacity: 1 },
                },
                ['card.show.all']: {
                  y: '0vh',
                  scale: 1,
                  opacity: 1,
                  transitionEnd: { opacity: 1 },
                },
                ['icon.show.all']: {
                  y: '0vh',
                  scale: 1,
                  opacity: 1,
                  transitionEnd: { opacity: 1 },
                },
              }
        }
      >
        <motion.div
          className='flex'
          variants={
            applyExitTransition
              ? {}
              : {
                  ['icon.unsnap.all']: {
                    color: color,
                    //@ts-ignore
                    '--fa-secondary-color': secondaryColor,
                  },
                  [`icon.unsnap.${interaction.id}`]: {
                    color: color,
                    //@ts-ignore
                    '--fa-secondary-color': secondaryColor,
                  },
                  [`icon.snap.${interaction.id}`]: {
                    color: snappedFgColor,
                    //@ts-ignore
                    '--fa-secondary-color': snappedFgColor,
                    filter: null,
                  },
                }
          }
        >
          <Icon
            css={css`
              width: ${dimensions.actionsPane.idock.widthRem - 2.25}rem;
              height: ${dimensions.actionsPane.idock.widthRem - 2.25}rem;
            `}
          />
        </motion.div>
        <AnimatePresence>
          {showBadge && !submitting && !isEditAndPaused && (
            <motion.div
              key={`${interaction.id}-badge`}
              css={css`
                content: '';
                position: absolute;
                top: -5px;
                right: -5px;
                border-radius: 50%;
                height: 1rem;
                width: 1rem;
                background: ${etheme.controls.badge.bg};
                border: 1px solid ${etheme.controls.badge.fg};
              `}
              exit={{
                scale: 2.5,
                opacity: 0,
                transition: transitions['icon.badge.exit'],
              }}
            />
          )}
        </AnimatePresence>
      </motion.div>
    </>
  );
});
